import React from 'react';
import { IconButton, Tooltip, Switch } from '@mui/material';
import { Brightness7, Brightness4 } from '@mui/icons-material';
import { useTheme } from './ThemeProvider';

// Enhanced Theme Toggle Icon with dynamic colors
export const ThemeToggleIcon = ({ sx = {} }) => {
  const { mode, toggleTheme } = useTheme();

  return (
    <Tooltip title={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}>
      <IconButton
        onClick={toggleTheme}
        sx={{
          ml: 1,
          borderRadius: '12px',
          background: 'rgba(191, 155, 48, 0.05)',
          border: '1px solid rgba(191, 155, 48, 0.1)',
          // Dynamic color based on theme mode
          color: mode === 'light' ? '#bf9b30' : '#f4d03f',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            background: 'rgba(191, 155, 48, 0.1)',
            borderColor: 'rgba(191, 155, 48, 0.3)',
            transform: 'scale(1.05)',
            boxShadow: '0 4px 12px rgba(191, 155, 48, 0.3)',
            // Ensure hover color is visible
            color: mode === 'light' ? '#9e7c0c' : '#f7dc6f',
          },
          // Override any inherited colors
          '& .MuiSvgIcon-root': {
            color: 'inherit',
          },
          ...sx
        }}
        aria-label="toggle dark mode"
      >
        {mode === 'light' ? <Brightness4 /> : <Brightness7 />}
      </IconButton>
    </Tooltip>
  );
};

// Enhanced Theme Toggle Switch with dynamic colors
export const ThemeToggleSwitch = ({ sx = {} }) => {
  const { mode, toggleTheme } = useTheme();

  return (
    <Tooltip title={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}>
      <Switch
        checked={mode === 'dark'}
        onChange={toggleTheme}
        sx={{
          // Custom switch styling with theme-aware colors
          '& .MuiSwitch-switchBase': {
            color: mode === 'light' ? '#bf9b30' : '#f4d03f',
            '&.Mui-checked': {
              color: mode === 'light' ? '#f4d03f' : '#bf9b30',
              '& + .MuiSwitch-track': {
                backgroundColor: mode === 'light' ? '#bf9b30' : '#f4d03f',
                opacity: 0.7,
              },
            },
          },
          '& .MuiSwitch-track': {
            backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.38)' : 'rgba(255, 255, 255, 0.38)',
          },
          ...sx
        }}
        inputProps={{ 'aria-label': 'toggle dark mode' }}
      />
    </Tooltip>
  );
};

// Alternative minimal version if you prefer simpler styling
export const MinimalThemeToggle = ({ sx = {} }) => {
  const { mode, toggleTheme } = useTheme();

  return (
    <Tooltip title={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}>
      <IconButton
        onClick={toggleTheme}
        size="small"
        sx={{
          // Simple but effective color fix
          color: mode === 'light' ? '#000000' : '#ffffff',
          backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
          '&:hover': {
            backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
            color: mode === 'light' ? '#333333' : '#cccccc',
          },
          ...sx
        }}
        aria-label="toggle dark mode"
      >
        {mode === 'light' ? <Brightness4 /> : <Brightness7 />}
      </IconButton>
    </Tooltip>
  );
};

export default ThemeToggleIcon;